import { CREDIT_LIMIT_COPY } from 'features/Enterprise/Vendor/constants/creditLimitCopy'
import type {
  DocumentType,
  VendorDocumentType,
} from 'features/Enterprise/Vendor/types/VendorManagement'

export const CREDIT_LIMIT_DOCUMENT_TYPE = 'credit_limit' as const satisfies DocumentType

export const initialCreditLimitDocData: VendorDocumentType = {
  file_path: '',
  file_name: '',
  document_type: CREDIT_LIMIT_DOCUMENT_TYPE,
  company_id: 0,
  label: CREDIT_LIMIT_COPY.document.label,
  required: 1,
}

const hasUploadedCreditLimitFile = (documentEntry: VendorDocumentType) =>
  Boolean(
    String(documentEntry.file_path ?? '').trim() || String(documentEntry.file_name ?? '').trim(),
  )

export const normalizeCreditLimitDocumentEntry = (
  documentEntry: VendorDocumentType,
): VendorDocumentType => ({
  ...documentEntry,
  label: initialCreditLimitDocData.label,
  document_type: CREDIT_LIMIT_DOCUMENT_TYPE,
  required: documentEntry.required ?? initialCreditLimitDocData.required,
})

export const pickCreditLimitDocumentFromList = (
  documents: VendorDocumentType[] | undefined,
): VendorDocumentType | undefined => {
  const matches =
    documents?.filter(
      (documentEntry) => documentEntry.document_type === CREDIT_LIMIT_DOCUMENT_TYPE,
    ) ?? []
  if (!matches.length) return undefined
  const withFile = matches.find(hasUploadedCreditLimitFile)
  return withFile ?? matches[0]
}

export const findVendorDocumentByType = (
  documents: VendorDocumentType[] | undefined,
  documentType: DocumentType | string,
) => documents?.find((documentEntry) => documentEntry.document_type === documentType)

export const upsertVendorDocumentByType = (
  documents: VendorDocumentType[],
  docData: VendorDocumentType,
): VendorDocumentType[] => {
  if (docData.document_type === CREDIT_LIMIT_DOCUMENT_TYPE) {
    const withoutCreditLimit = documents.filter(
      (documentEntry) => documentEntry.document_type !== CREDIT_LIMIT_DOCUMENT_TYPE,
    )
    return [...withoutCreditLimit, normalizeCreditLimitDocumentEntry(docData)]
  }

  const hasExisting = findVendorDocumentByType(documents, docData.document_type)
  if (hasExisting) {
    return documents.map((documentEntry) =>
      documentEntry.document_type === docData.document_type ? docData : documentEntry,
    )
  }
  return [...documents, docData]
}

export const mapCreditLimitDocumentsForForm = (
  documents: VendorDocumentType[] | undefined,
): VendorDocumentType[] => {
  const picked = pickCreditLimitDocumentFromList(documents)
  return picked ? [normalizeCreditLimitDocumentEntry(picked)] : []
}

export const dedupeCreditLimitDocumentsForSubmission = (
  documents: VendorDocumentType[],
): VendorDocumentType[] => {
  const creditLimitDoc = pickCreditLimitDocumentFromList(documents)
  const withoutCreditLimit = documents.filter(
    (documentEntry) => documentEntry.document_type !== CREDIT_LIMIT_DOCUMENT_TYPE,
  )
  if (!creditLimitDoc) return withoutCreditLimit
  return [...withoutCreditLimit, normalizeCreditLimitDocumentEntry(creditLimitDoc)]
}

export const filterCreditLimitDocumentsFromList = (
  documents: VendorDocumentType[] | undefined,
): VendorDocumentType[] =>
  documents?.filter(
    (documentEntry) => documentEntry.document_type === CREDIT_LIMIT_DOCUMENT_TYPE,
  ) ?? []

export type VendorDocumentSlicesType = {
  pksDocs: VendorDocumentType[]
  nonPkpDocs: VendorDocumentType[]
  otherDocs: VendorDocumentType[]
  creditLimitDocs: VendorDocumentType[]
}

export const mergeVendorDocumentSlices = (
  slices: VendorDocumentSlicesType,
): VendorDocumentType[] => [
  ...slices.pksDocs,
  ...slices.nonPkpDocs,
  ...slices.otherDocs,
  ...slices.creditLimitDocs,
]
